import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useSelection } from '../context/SelectionContext';
import { useTheme } from '../context/ThemeContext';
import { productsApi } from '../lib/api';
import { trackAddToSelection } from '../lib/analytics';
import { Plus, Check, X, ChevronLeft, ChevronRight } from 'lucide-react';

const productPlaceholders = [
  'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&q=80',
  'https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?w=400&q=80',
  'https://images.unsplash.com/photo-1473093295043-cdd812d0e601?w=400&q=80',
];

const titles = {
  es: 'Combina con…',
  en: 'Pairs well with…',
  cat: 'Combina amb…',
  fr: 'Se marie avec…',
};

const CrossSellStrip = ({ triggerProduct, onClose }) => {
  const { language, localize } = useLanguage();
  const { addItem } = useSelection();
  const { isDark } = useTheme();
  const [suggestions, setSuggestions] = useState([]);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [addedIds, setAddedIds] = useState(new Set());
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  useEffect(() => {
    if (!triggerProduct?.id) return;

    const fetchSuggestions = async () => {
      try {
        const res = await productsApi.getRecommendations(triggerProduct.id);
        const items = (res.data || []).filter(p => p.id !== triggerProduct.id);
        if (items.length > 0) {
          setSuggestions(items.slice(0, 3));
          setAddedIds(new Set());
          setActiveIndex(0);
          setVisible(true);
          setExiting(false);
          timerRef.current = setTimeout(() => handleDismiss(), 8000);
        }
      } catch (e) {
        // silent
      }
    };

    fetchSuggestions();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [triggerProduct?.id]);

  const handleDismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setExiting(true);
    setTimeout(() => {
      setVisible(false);
      setExiting(false);
      onClose();
    }, 300);
  }, [onClose]);

  const handleAdd = useCallback((product) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    trackAddToSelection(product, 1, []);
    addItem(product, 1, []);
    setAddedIds(prev => new Set([...prev, product.id]));
    // Auto-advance to next or dismiss
    setTimeout(() => {
      if (activeIndex < suggestions.length - 1) {
        setActiveIndex(prev => prev + 1);
        timerRef.current = setTimeout(() => handleDismiss(), 6000);
      } else {
        handleDismiss();
      }
    }, 500);
  }, [addItem, handleDismiss, activeIndex, suggestions.length]);

  const goNext = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (activeIndex < suggestions.length - 1) {
      setActiveIndex(prev => prev + 1);
    } else {
      handleDismiss();
    }
    timerRef.current = setTimeout(() => handleDismiss(), 6000);
  }, [activeIndex, suggestions.length, handleDismiss]);

  const goPrev = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (activeIndex > 0) setActiveIndex(prev => prev - 1);
    timerRef.current = setTimeout(() => handleDismiss(), 6000);
  }, [activeIndex, handleDismiss]);

  // Swipe gesture handling
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    // Horizontal swipe
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) goNext();
      else goPrev();
    }
    // Swipe down to dismiss
    if (dy > 60 && Math.abs(dy) > Math.abs(dx)) {
      handleDismiss();
    }
  }, [goNext, goPrev, handleDismiss]);

  const getImage = (product, idx) => {
    if (product.image_url && product.image_url.startsWith('http')) return product.image_url;
    if (product.images?.[0]) return product.images[0];
    return productPlaceholders[idx % productPlaceholders.length];
  };

  if (!visible || suggestions.length === 0) return null;

  const product = suggestions[activeIndex];
  const isAdded = addedIds.has(product.id);

  return (
    <div
      data-testid="cross-sell-strip"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 92,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        animation: exiting ? 'crossSellFadeOut 0.3s ease forwards' : 'crossSellFadeIn 0.3s ease',
      }}
    >
      {/* Backdrop — dark overlay */}
      <div
        onClick={handleDismiss}
        data-testid="cross-sell-backdrop"
        style={{
          position: 'absolute',
          inset: 0,
          background: isDark ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      />

      {/* Close button — always visible, top right */}
      <button
        onClick={handleDismiss}
        data-testid="cross-sell-dismiss"
        style={{
          position: 'absolute',
          top: '60px',
          right: '20px',
          zIndex: 3,
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(255,255,255,0.15)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          backdropFilter: 'blur(10px)',
        }}
      >
        <X size={18} />
      </button>

      {/* Title */}
      <p
        style={{
          position: 'relative',
          zIndex: 2,
          fontFamily: "'Playfair Display', serif",
          fontSize: '18px',
          fontWeight: 500,
          color: '#fff',
          marginBottom: '20px',
          opacity: 0.9,
          letterSpacing: '0.02em',
        }}
      >
        {titles[language] || titles.es}
      </p>

      {/* Showcase card — centered, large */}
      <div
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          position: 'relative',
          zIndex: 2,
          width: 'min(85vw, 320px)',
          animation: 'crossSellCardEnter 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
          key: product.id,
        }}
        data-testid={`cross-sell-item-${product.id}`}
      >
        <div
          style={{
            borderRadius: '20px',
            overflow: 'hidden',
            background: isDark ? '#1e1e1e' : '#FBF5E8',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(183,140,60,0.15)',
          }}
        >
          {/* Large product image */}
          <div style={{
            width: '100%',
            height: '220px',
            overflow: 'hidden',
            position: 'relative',
          }}>
            <img
              src={getImage(product, activeIndex)}
              alt={localize(product.name) || ''}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
              loading="eager"
              draggable={false}
            />
            {/* Gradient overlay on image */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '80px',
              background: isDark
                ? 'linear-gradient(transparent, #1e1e1e)'
                : 'linear-gradient(transparent, #FBF5E8)',
            }} />
          </div>

          {/* Product info */}
          <div style={{ padding: '16px 20px 20px' }}>
            <h3 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '20px',
              fontWeight: 600,
              lineHeight: 1.25,
              color: isDark ? '#f5f5f5' : '#2C2C2A',
              marginBottom: '8px',
            }}>
              {localize(product.name)}
            </h3>

            {product.description && (
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '13px',
                lineHeight: 1.4,
                color: isDark ? 'rgba(255,255,255,0.5)' : '#8C8C89',
                marginBottom: '14px',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
                {localize(product.description)}
              </p>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <span style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '22px',
                fontWeight: 700,
                color: isDark ? '#D4AF37' : '#B78C3C',
                letterSpacing: '-0.02em',
              }}>
                {product.price?.toFixed(2)}€
              </span>

              <button
                onClick={(e) => { e.stopPropagation(); handleAdd(product); }}
                data-testid={`cross-sell-add-${product.id}`}
                style={{
                  height: '44px',
                  paddingLeft: isAdded ? '18px' : '20px',
                  paddingRight: isAdded ? '18px' : '20px',
                  borderRadius: '22px',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                  background: isAdded
                    ? '#4ade80'
                    : 'linear-gradient(135deg, #8B4513, #C89B3C)',
                  color: '#FBF5E8',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px',
                  fontWeight: 600,
                  boxShadow: isAdded
                    ? '0 4px 15px rgba(74, 222, 128, 0.3)'
                    : '0 4px 15px rgba(183, 140, 60, 0.35)',
                  transform: isAdded ? 'scale(1.03)' : 'scale(1)',
                }}
              >
                {isAdded ? <Check size={18} /> : <Plus size={18} />}
                {isAdded ? '' : (language === 'es' ? 'Añadir' : language === 'cat' ? 'Afegir' : language === 'fr' ? 'Ajouter' : 'Add')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dot indicators */}
      {suggestions.length > 1 && (
        <div style={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          gap: '8px',
          marginTop: '20px',
        }}>
          {suggestions.map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                if (timerRef.current) clearTimeout(timerRef.current);
                setActiveIndex(idx);
                timerRef.current = setTimeout(() => handleDismiss(), 6000);
              }}
              style={{
                width: idx === activeIndex ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                background: idx === activeIndex
                  ? 'linear-gradient(135deg, #B78C3C, #D4AF37)'
                  : 'rgba(255,255,255,0.3)',
              }}
              data-testid={`cross-sell-dot-${idx}`}
            />
          ))}
        </div>
      )}

      {/* Navigation hint */}
      {suggestions.length > 1 && (
        <p style={{
          position: 'relative',
          zIndex: 2,
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '12px',
          color: 'rgba(255,255,255,0.35)',
          marginTop: '12px',
          letterSpacing: '0.04em',
        }}>
          {activeIndex + 1} / {suggestions.length}
        </p>
      )}
    </div>
  );
};

export default CrossSellStrip;
